import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { adminApi, clearToken, getToken } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Fingerprint, LogOut, CalendarDays, Users, Download, Search, XCircle,
  Loader2, MailWarning, MailCheck, Send, Eye, RefreshCw, Inbox, Settings,
  MessageSquare, MailOpen, Mail, BellRing, KeyRound, UserPlus, Trash2, ShieldCheck, Printer,
} from "lucide-react";
import { toast } from "sonner";

const SERVICES_LIST = [
  "Traditional Ink Fingerprint Cards",
  "Live Scan Digital Fingerprinting",
  "FDLE Digital Submissions",
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  // Messages
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  // Password change
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwError, setPwError] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  // Team management
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: "", email: "", password: "" });
  const [staffError, setStaffError] = useState("");
  const [staffSaving, setStaffSaving] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSaving, setResetSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin");
    }
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/stats");
      setStats(res.data);
    } catch (e) { /* handled by interceptor */ }
  }, []);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter) params.date = dateFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (serviceFilter !== "all") params.service = serviceFilter;
      if (search.trim()) params.search = search.trim();
      const res = await adminApi.get("/admin/appointments", { params });
      setAppointments(res.data.appointments);
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Failed to load appointments.");
    } finally {
      setLoading(false);
    }
  }, [dateFilter, statusFilter, serviceFilter, search]);

  const fetchMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const res = await adminApi.get("/admin/contact-messages");
      setMessages(res.data.messages);
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Failed to load messages.");
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await adminApi.get("/auth/me");
      setCurrentUser(res.data);
    } catch (e) { /* handled by interceptor */ }
  }, []);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await adminApi.get("/admin/users");
      setUsers(res.data.users);
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Failed to load team members.");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchMessages();
    fetchCurrentUser();
    fetchUsers();
  }, [fetchStats, fetchMessages, fetchCurrentUser, fetchUsers]);

  useEffect(() => {
    const t = setTimeout(fetchAppointments, 300);
    return () => clearTimeout(t);
  }, [fetchAppointments]);

  const handleLogout = () => {
    clearToken();
    navigate("/admin");
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCanceling(true);
    try {
      await adminApi.put(`/admin/appointments/${cancelTarget.id}/cancel`);
      toast.success(`Appointment ${cancelTarget.confirmation_number} canceled.`);
      setCancelTarget(null);
      fetchAppointments();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel appointment.");
    } finally {
      setCanceling(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = dateFilter ? { date: dateFilter } : {};
      const res = await adminApi.get("/admin/appointments/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `appointments_${dateFilter || "all"}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported.");
    } catch (e) {
      toast.error("Export failed.");
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await adminApi.post("/admin/send-daily-digest");
      if (res.data.sent) {
        toast.success(`Digest sent for ${res.data.for_date} (${res.data.count} appointments) to ${res.data.recipients.join(", ")}`);
      } else {
        toast.warning(`Digest prepared (${res.data.count} appointments for ${res.data.for_date}) but email not sent: ${res.data.reason}`);
      }
    } catch (e) {
      toast.error("Failed to run digest.");
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await adminApi.post("/admin/send-reminders");
      if (res.data.eligible === 0) {
        toast.info(`No reminders needed \u2014 all of today's customers have already been reminded (or no appointments today).`);
      } else if (res.data.sent > 0) {
        toast.success(`Reminders sent to ${res.data.sent} customer${res.data.sent > 1 ? "s" : ""} for today (${res.data.for_date}).`);
      } else {
        toast.warning(`${res.data.eligible} reminder(s) attempted but none sent \u2014 check email configuration.`);
      }
    } catch (e) {
      toast.error("Failed to send reminders.");
    } finally {
      setSendingReminders(false);
    }
  };

  const handleToggleRead = async (msg) => {
    try {
      const res = await adminApi.put(`/admin/contact-messages/${msg.id}/read`);
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read: res.data.read } : m)));
      fetchStats();
    } catch (e) {
      toast.error("Failed to update message.");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (!pwForm.current || !pwForm.next) {
      setPwError("Please fill in all fields.");
      return;
    }
    if (pwForm.next.length < 8) {
      setPwError("New password must be at least 8 characters.");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwError("New passwords do not match.");
      return;
    }
    setPwSaving(true);
    try {
      await adminApi.post("/auth/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      toast.success("Password changed successfully. Use your new password next time you log in.");
      setPwOpen(false);
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwError(err.response?.data?.detail || "Failed to change password.");
    } finally {
      setPwSaving(false);
    }
  };

  const clearFilters = () => {
    setDateFilter("");
    setStatusFilter("all");
    setServiceFilter("all");
    setSearch("");
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    setStaffError("");
    if (!staffForm.name || staffForm.name.trim().length < 2) {
      setStaffError("Please enter the staff member's name.");
      return;
    }
    if (!staffForm.email || !/^\S+@\S+\.\S+$/.test(staffForm.email)) {
      setStaffError("Please enter a valid email address.");
      return;
    }
    if (!staffForm.password || staffForm.password.length < 8) {
      setStaffError("Password must be at least 8 characters.");
      return;
    }
    setStaffSaving(true);
    try {
      await adminApi.post("/admin/users", staffForm);
      toast.success(`Staff account created for ${staffForm.email}. Share their login details securely.`);
      setAddStaffOpen(false);
      setStaffForm({ name: "", email: "", password: "" });
      fetchUsers();
    } catch (err) {
      setStaffError(err.response?.data?.detail || "Failed to create staff account.");
    } finally {
      setStaffSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget) return;
    setDeletingUser(true);
    try {
      await adminApi.delete(`/admin/users/${deleteUserTarget.id}`);
      toast.success(`${deleteUserTarget.email} removed from the team.`);
      setDeleteUserTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove staff account.");
    } finally {
      setDeletingUser(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    if (!resetPw || resetPw.length < 8) {
      setResetError("New password must be at least 8 characters.");
      return;
    }
    setResetSaving(true);
    try {
      await adminApi.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetTarget.email}.`);
      setResetTarget(null);
      setResetPw("");
    } catch (err) {
      setResetError(err.response?.data?.detail || "Failed to reset password.");
    } finally {
      setResetSaving(false);
    }
  };

  const isOwner = currentUser?.role === "owner";

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-white.png" alt="Cayman Fingerprinting Services Ltd." className="h-10 w-auto" />
            <span className="hidden md:block text-[11px] text-white/50 border-l border-white/20 pl-3">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Link to="/" className="text-xs text-white/60 hover:text-white mr-2 transition-colors duration-200">View Website</Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPwOpen(true)}
              className="text-white hover:bg-white/10 hover:text-white"
              data-testid="admin-settings-button"
            >
              <Settings className="h-4 w-4 mr-1.5" /> Settings
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10 hover:text-white" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Email status banner */}
        {stats && !stats.email_configured && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="admin-email-disabled-banner">
            <MailWarning className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Email sending is not configured yet</p>
              <p className="mt-0.5 text-amber-800/80">
                Bookings work normally, but confirmation emails, reminders, and the daily 7 PM digest to{" "}
                {stats.digest_recipients.join(" & ")} will not send until email credentials are added.
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Today</p>
                <CalendarDays className="h-4 w-4 text-[color:var(--brand-sky)]" />
              </div>
              <p className="text-3xl font-heading font-semibold text-[color:var(--brand-navy)] mt-2" data-testid="admin-stat-today">{stats?.today ?? "\u2013"}</p>
              <p className="text-xs text-muted-foreground mt-1">appointments today</p>
            </CardContent>
          </Card>
          <Card className="border bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Tomorrow</p>
                <CalendarDays className="h-4 w-4 text-[color:var(--brand-sky)]" />
              </div>
              <p className="text-3xl font-heading font-semibold text-[color:var(--brand-navy)] mt-2" data-testid="admin-stat-tomorrow">{stats?.tomorrow ?? "\u2013"}</p>
              <p className="text-xs text-muted-foreground mt-1">appointments tomorrow</p>
            </CardContent>
          </Card>
          <Card className="border bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Upcoming</p>
                <Users className="h-4 w-4 text-[color:var(--brand-sky)]" />
              </div>
              <p className="text-3xl font-heading font-semibold text-[color:var(--brand-navy)] mt-2" data-testid="admin-stat-upcoming">{stats?.upcoming ?? "\u2013"}</p>
              <p className="text-xs text-muted-foreground mt-1">booked from today onward</p>
            </CardContent>
          </Card>
          <Card className="border bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Automations</p>
                {stats?.email_configured ? <MailCheck className="h-4 w-4 text-green-600" /> : <MailWarning className="h-4 w-4 text-amber-500" />}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 leading-snug">Digest 7 PM \u00b7 Reminders 7 AM</p>
              <div className="flex gap-1.5 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs flex-1 btn-lift px-2"
                  onClick={handleSendDigest}
                  disabled={sendingDigest}
                  data-testid="admin-send-digest-button"
                >
                  {sendingDigest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                  Digest
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs flex-1 btn-lift px-2"
                  onClick={handleSendReminders}
                  disabled={sendingReminders}
                  data-testid="admin-send-reminders-button"
                >
                  {sendingReminders ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5 mr-1" />}
                  Remind
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs: Appointments | Messages */}
        <Tabs defaultValue="appointments" className="w-full">
          <TabsList className="mb-4 bg-secondary" data-testid="admin-tabs">
            <TabsTrigger value="appointments" data-testid="admin-tab-appointments">
              <CalendarDays className="h-4 w-4 mr-1.5" /> Appointments
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">
              <MessageSquare className="h-4 w-4 mr-1.5" /> Messages
              {unreadCount > 0 && (
                <Badge className="ml-2 bg-[color:var(--brand-sky)] text-white hover:bg-[color:var(--brand-sky)] px-1.5 py-0 text-[10px]" data-testid="admin-unread-badge">
                  {unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="admin-tab-team">
              <Users className="h-4 w-4 mr-1.5" /> Team
            </TabsTrigger>
          </TabsList>

          {/* ---- Appointments tab ---- */}
          <TabsContent value="appointments">
            <Card className="border bg-white mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search name, email, phone, confirmation #..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                      data-testid="admin-search-input"
                    />
                  </div>
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-auto"
                    data-testid="admin-filter-date"
                  />
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px]" data-testid="admin-filter-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="booked">Booked</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={serviceFilter} onValueChange={setServiceFilter}>
                    <SelectTrigger className="w-[220px]" data-testid="admin-filter-service">
                      <SelectValue placeholder="Service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All services</SelectItem>
                      {SERVICES_LIST.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="admin-clear-filters-button">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Clear
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto btn-lift"
                    onClick={() => {
                      const d = dateFilter || new Date().toISOString().slice(0, 10);
                      window.open(`/admin/print?date=${d}&autoprint=1`, "_blank");
                    }}
                    data-testid="admin-print-schedule-button"
                  >
                    <Printer className="h-4 w-4 mr-1.5" /> Print Schedule
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExport} className="btn-lift" data-testid="admin-export-csv-button">
                    <Download className="h-4 w-4 mr-1.5" /> Export CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border bg-white">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-20 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading appointments...
                  </div>
                ) : appointments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="admin-empty-state">
                    <Inbox className="h-10 w-10 mb-3 text-muted-foreground/50" />
                    <p className="font-medium">No appointments found</p>
                    <p className="text-sm mt-1">
                      {dateFilter || statusFilter !== "all" || serviceFilter !== "all" || search
                        ? "Try adjusting or clearing your filters."
                        : "New bookings will appear here."}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-appointments-table">
                      <TableHeader>
                        <TableRow className="bg-secondary/60">
                          <TableHead className="font-semibold">Conf. #</TableHead>
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold">Time</TableHead>
                          <TableHead className="font-semibold">Service</TableHead>
                          <TableHead className="font-semibold">Customer</TableHead>
                          <TableHead className="font-semibold">Phone</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {appointments.map((a) => (
                          <TableRow key={a.id} data-testid="admin-appointment-row">
                            <TableCell className="font-mono-slot text-xs">{a.confirmation_number}</TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{a.date}</TableCell>
                            <TableCell className="text-sm whitespace-nowrap font-mono-slot">{a.time_display}</TableCell>
                            <TableCell className="text-sm max-w-[180px] truncate" title={a.service}>{a.service}</TableCell>
                            <TableCell className="text-sm">
                              <span className="font-medium">{a.name}</span>
                              <span className="block text-xs text-muted-foreground">{a.email}</span>
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{a.phone}</TableCell>
                            <TableCell>
                              {a.status === "booked" ? (
                                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Booked</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-muted-foreground">Canceled</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <Button variant="ghost" size="sm" onClick={() => setDetailTarget(a)} data-testid="admin-view-appointment-button">
                                <Eye className="h-4 w-4" />
                              </Button>
                              {a.status === "booked" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive hover:bg-red-50"
                                  onClick={() => setCancelTarget(a)}
                                  data-testid="admin-cancel-appointment-button"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- Messages tab ---- */}
          <TabsContent value="messages">
            <Card className="border bg-white">
              <CardContent className="p-0">
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-20 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading messages...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="admin-messages-empty-state">
                    <MessageSquare className="h-10 w-10 mb-3 text-muted-foreground/50" />
                    <p className="font-medium">No messages yet</p>
                    <p className="text-sm mt-1">Contact form submissions from the website will appear here.</p>
                  </div>
                ) : (
                  <ul className="divide-y" data-testid="admin-messages-list">
                    {messages.map((m) => (
                      <li key={m.id} className={`p-5 ${!m.read ? "bg-blue-50/40" : ""}`} data-testid="admin-message-item">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {!m.read && <span className="h-2 w-2 rounded-full bg-[color:var(--brand-sky)] shrink-0" title="Unread" />}
                              <p className="font-heading font-semibold text-sm text-[color:var(--brand-navy)]">{m.name}</p>
                              <a href={`mailto:${m.email}`} className="text-xs text-[color:var(--brand-sky)] hover:underline">{m.email}</a>
                              {m.phone && <span className="text-xs text-muted-foreground">\u00b7 {m.phone}</span>}
                            </div>
                            <p className="mt-2 text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap break-words">{m.message}</p>
                            <p className="mt-2 text-xs text-muted-foreground">
                              {m.created_at ? format(new Date(m.created_at), "PPpp") : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs btn-lift"
                              onClick={() => handleToggleRead(m)}
                              data-testid="admin-message-toggle-read-button"
                            >
                              {m.read ? (
                                <><Mail className="h-3.5 w-3.5 mr-1.5" /> Mark Unread</>
                              ) : (
                                <><MailOpen className="h-3.5 w-3.5 mr-1.5" /> Mark Read</>
                              )}
                            </Button>
                            <Button variant="outline" size="sm" className="h-8 text-xs btn-lift" asChild>
                              <a href={`mailto:${m.email}?subject=Re: Your inquiry - Cayman Fingerprinting Services`}>Reply</a>
                            </Button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- Team tab ---- */}
          <TabsContent value="team">
            <Card className="border bg-white">
              <CardContent className="p-0">
                <div className="flex items-center justify-between p-5 border-b flex-wrap gap-3">
                  <div>
                    <h3 className="font-heading font-semibold text-[color:var(--brand-navy)]">Team Members</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isOwner
                        ? "Add staff accounts so each person has their own login. Staff can manage appointments and messages."
                        : "Team member list. Only the owner account can add or remove staff."}
                    </p>
                  </div>
                  {isOwner && (
                    <Button
                      size="sm"
                      className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] btn-lift"
                      onClick={() => setAddStaffOpen(true)}
                      data-testid="admin-add-staff-button"
                    >
                      <UserPlus className="h-4 w-4 mr-1.5" /> Add Staff
                    </Button>
                  )}
                </div>
                {usersLoading ? (
                  <div className="flex items-center justify-center py-16 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading team...
                  </div>
                ) : (
                  <ul className="divide-y" data-testid="admin-team-list">
                    {users.map((u) => (
                      <li key={u.id} className="p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-team-member">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center shrink-0">
                            {u.role === "owner" ? (
                              <ShieldCheck className="h-5 w-5 text-[color:var(--brand-navy)]" />
                            ) : (
                              <Users className="h-5 w-5 text-[color:var(--brand-navy)]" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-heading font-semibold text-sm text-[color:var(--brand-navy)]">{u.name}</p>
                              {u.role === "owner" ? (
                                <Badge className="bg-[color:var(--brand-navy)] text-white hover:bg-[color:var(--brand-navy)] text-[10px]">Owner</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-[10px]">Staff</Badge>
                              )}
                              {currentUser?.email === u.email && (
                                <Badge variant="outline" className="text-[10px] text-muted-foreground">You</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{u.email}</p>
                            <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                              {u.last_login ? `Last login: ${format(new Date(u.last_login), "PPp")}` : "Never logged in"}
                            </p>
                          </div>
                        </div>
                        {isOwner && u.role !== "owner" && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs btn-lift"
                              onClick={() => { setResetTarget(u); setResetPw(""); setResetError(""); }}
                              data-testid="admin-reset-staff-password-button"
                            >
                              <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Reset Password
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs text-destructive hover:text-destructive hover:bg-red-50 btn-lift"
                              onClick={() => setDeleteUserTarget(u)}
                              data-testid="admin-delete-staff-button"
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                            </Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Cancel confirmation */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <AlertDialogContent data-testid="admin-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget && (
                <>
                  {cancelTarget.name} &mdash; {cancelTarget.service} on {cancelTarget.date} at {cancelTarget.time_display}.
                  This will free the slot for other customers. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-cancel-dialog-dismiss">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCancel(); }}
              className="bg-destructive hover:bg-destructive/90"
              disabled={canceling}
              data-testid="admin-cancel-dialog-confirm"
            >
              {canceling ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Cancel Appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Detail dialog */}
      <Dialog open={!!detailTarget} onOpenChange={(open) => !open && setDetailTarget(null)}>
        <DialogContent data-testid="admin-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[color:var(--brand-navy)]">Appointment Details</DialogTitle>
            <DialogDescription>Full booking information</DialogDescription>
          </DialogHeader>
          {detailTarget && (
            <div className="space-y-3 text-sm">
              {[
                ["Confirmation #", detailTarget.confirmation_number],
                ["Status", detailTarget.status === "booked" ? "Booked" : "Canceled"],
                ["Service", detailTarget.service],
                ["Date", detailTarget.date_display || detailTarget.date],
                ["Time", detailTarget.time_display],
                ["Customer", detailTarget.name],
                ["Email", detailTarget.email],
                ["Phone", detailTarget.phone],
                ["Notes", detailTarget.notes || "\u2014"],
                ["Reminder sent", detailTarget.reminder_sent ? "Yes" : "Not yet"],
                ["Booked at", detailTarget.created_at ? format(new Date(detailTarget.created_at), "PPpp") : "\u2014"],
              ].map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-4 py-1.5 border-b border-border/60 last:border-0">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-medium text-right text-[color:var(--brand-navy)] break-all">{value}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={pwOpen} onOpenChange={(open) => { setPwOpen(open); if (!open) { setPwError(""); setPwForm({ current: "", next: "", confirm: "" }); } }}>
        <DialogContent data-testid="admin-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[color:var(--brand-navy)] flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[color:var(--brand-sky)]" /> Change Password
            </DialogTitle>
            <DialogDescription>Update your admin login password. You'll use the new password next time you sign in.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pw-current">Current password</Label>
              <Input
                id="pw-current"
                type="password"
                value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                data-testid="admin-password-current-input"
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw-next">New password (min 8 characters)</Label>
              <Input
                id="pw-next"
                type="password"
                value={pwForm.next}
                onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                data-testid="admin-password-new-input"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw-confirm">Confirm new password</Label>
              <Input
                id="pw-confirm"
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                data-testid="admin-password-confirm-input"
                autoComplete="new-password"
              />
            </div>
            {pwError && <p className="text-sm text-destructive" role="alert" aria-live="polite" data-testid="admin-password-error">{pwError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)]"
                disabled={pwSaving}
                data-testid="admin-password-submit-button"
              >
                {pwSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Update Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add staff dialog */}
      <Dialog open={addStaffOpen} onOpenChange={(open) => { setAddStaffOpen(open); if (!open) { setStaffError(""); setStaffForm({ name: "", email: "", password: "" }); } }}>
        <DialogContent data-testid="admin-add-staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[color:var(--brand-navy)] flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[color:var(--brand-sky)]" /> Add Staff Member
            </DialogTitle>
            <DialogDescription>
              Create a login for a team member. They can manage appointments and messages, but cannot add or remove staff.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddStaff} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="staff-name">Full name</Label>
              <Input
                id="staff-name"
                placeholder="e.g., Jane Smith"
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                data-testid="admin-staff-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="staff-email">Email address (their login)</Label>
              <Input
                id="staff-email"
                type="email"
                placeholder="e.g., jane@caymanfingerprinting.ky"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                data-testid="admin-staff-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="staff-password">Temporary password (min 8 characters)</Label>
              <Input
                id="staff-password"
                type="text"
                placeholder="They can change it after first login"
                value={staffForm.password}
                onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                data-testid="admin-staff-password-input"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">Share it with them securely — they can change it via Settings after logging in.</p>
            </div>
            {staffError && <p className="text-sm text-destructive" role="alert" aria-live="polite" data-testid="admin-staff-error">{staffError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddStaffOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)]"
                disabled={staffSaving}
                data-testid="admin-staff-submit-button"
              >
                {staffSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Create Account
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete staff confirmation */}
      <AlertDialog open={!!deleteUserTarget} onOpenChange={(open) => !open && setDeleteUserTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this staff account?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteUserTarget && (
                <>
                  {deleteUserTarget.name} ({deleteUserTarget.email}) will immediately lose access to the dashboard.
                  This cannot be undone, but you can create a new account for them anytime.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-staff-dismiss">Keep account</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteUser(); }}
              className="bg-destructive hover:bg-destructive/90"
              disabled={deletingUser}
              data-testid="admin-delete-staff-confirm"
            >
              {deletingUser ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Remove Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset staff password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => { if (!open) { setResetTarget(null); setResetPw(""); setResetError(""); } }}>
        <DialogContent data-testid="admin-reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[color:var(--brand-navy)] flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[color:var(--brand-sky)]" /> Reset Password
            </DialogTitle>
            <DialogDescription>
              {resetTarget && <>Set a new password for {resetTarget.name} ({resetTarget.email}).</>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reset-password">New password (min 8 characters)</Label>
              <Input
                id="reset-password"
                type="text"
                placeholder="New temporary password"
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
                data-testid="admin-reset-password-input"
                autoComplete="off"
              />
            </div>
            {resetError && <p className="text-sm text-destructive" role="alert" aria-live="polite">{resetError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
              <Button
                type="submit"
                className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)]"
                disabled={resetSaving}
                data-testid="admin-reset-password-submit"
              >
                {resetSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Reset Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
